import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiImage, FiUpload, FiX } from 'react-icons/fi';

const normalizePricingBasis = (value) => (value === 'kg' ? 'kg' : 'unit');

export const buildProductFormState = (product = null) => ({
  productName: product?.productName || '',
  categoryId: product?.categoryId ?? product?.Category?.id ?? '',
  subcategoryId: product?.subcategoryId ?? product?.Subcategory?.id ?? '',
  barcodeScope: product?.barcodeScope === 'per_variety' ? 'per_variety' : 'global',
  useDefaultTaxes: product?.useDefaultTaxes !== false,
  taxIds: Array.isArray(product?.taxIds) ? product.taxIds : [],
});

export const resolveProductFormPricingBasis = ({ override, category, product }) => {
  if (override === 'unit' || override === 'kg') return override;
  if (category) return normalizePricingBasis(category.pricingBasis);
  return normalizePricingBasis(product?.effectivePricingBasis);
};

const getErrorMessage = (error, fallback) => (
  error?.response?.data?.message
  || error?.response?.data?.error
  || error?.message
  || fallback
);

const getFocusableElements = (container) => Array.from(container.querySelectorAll(
  'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
)).filter((element) => element.getAttribute('aria-hidden') !== 'true');

const ProductFormDialog = ({
  isOpen = true,
  mode = 'create',
  initialProduct = null,
  categories = [],
  categoriesLoading = false,
  categoriesError = '',
  taxes = [],
  taxesLoading = false,
  taxesError = '',
  canReadTaxes = true,
  canSave = true,
  onClose,
  onSubmit,
}) => {
  const isEditing = mode === 'edit';
  const [form, setForm] = useState(() => buildProductFormState(initialProduct));
  const [imageFile, setImageFile] = useState(null);
  const [removeProductImage, setRemoveProductImage] = useState(false);
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');
  const [validationError, setValidationError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [standardCounts, setStandardCounts] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const dialogRef = useRef(null);
  const nameInputRef = useRef(null);
  const previousFocusRef = useRef(null);
  const productKey = initialProduct?.id ?? 'new';

  useEffect(() => {
    if (!isOpen) return;
    setForm(buildProductFormState(initialProduct));
    setStandardCounts(null);
    setImageFile(null);
    setRemoveProductImage(false);
    setValidationError('');
    setSubmitError('');
  }, [isOpen, isEditing, productKey]);

  useEffect(() => {
    if (!isOpen || isEditing || !taxesError) return;
    setForm((current) => (
      current.useDefaultTaxes
        ? current
        : { ...current, useDefaultTaxes: true, taxIds: [] }
    ));
  }, [isEditing, isOpen, taxesError]);

  useEffect(() => {
    if (!imageFile || typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') {
      setImagePreviewUrl('');
      return undefined;
    }
    const nextUrl = URL.createObjectURL(imageFile);
    setImagePreviewUrl(nextUrl);
    return () => URL.revokeObjectURL?.(nextUrl);
  }, [imageFile]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    previousFocusRef.current = document.activeElement;
    const focusTimer = window.setTimeout(() => nameInputRef.current?.focus(), 0);
    return () => {
      window.clearTimeout(focusTimer);
      document.body.style.overflow = previousOverflow;
      previousFocusRef.current?.focus?.();
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && !isSaving) onClose?.();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isSaving, onClose]);

  const categoryOptions = useMemo(() => {
    const selectedId = String(form.categoryId || '');
    return (categories || []).filter((category) => (
      category?.isActive !== false || (isEditing && String(category?.id) === selectedId)
    ));
  }, [categories, form.categoryId, isEditing]);
  const selectedCategory = useMemo(
    () => categoryOptions.find((category) => String(category?.id) === String(form.categoryId)) || null,
    [categoryOptions, form.categoryId]
  );
  const subcategoryOptions = useMemo(
    () => (selectedCategory?.CategorySubcategories || [])
      .slice()
      .sort((a, b) => (
        Number(a?.sortOrder || 0) - Number(b?.sortOrder || 0)
        || String(a?.name || '').localeCompare(String(b?.name || ''), 'es', { sensitivity: 'base' })
      )),
    [selectedCategory]
  );
  const taxOptions = useMemo(
    () => (taxes || []).filter((tax) => tax?.isActive !== false && tax?.branchActive !== false),
    [taxes]
  );
  const existingImage = removeProductImage ? '' : initialProduct?.productImage || '';
  const displayedImage = imagePreviewUrl || existingImage;
  const disabled = !canSave || isSaving || categoriesLoading;

  if (!isOpen || typeof document === 'undefined') return null;

  const requestClose = () => {
    if (!isSaving) onClose?.();
  };

  const handleDialogKeyDown = (event) => {
    if (event.key !== 'Tab') return;
    const focusable = getFocusableElements(dialogRef.current);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (disabled) return;
    const productName = form.productName.trim();
    if (!productName) {
      setValidationError('El nombre del producto es obligatorio.');
      nameInputRef.current?.focus();
      return;
    }
    if (!selectedCategory) {
      setValidationError('Selecciona una categoria.');
      return;
    }
    const selectedSubcategory = subcategoryOptions.find(
      (subcategory) => String(subcategory?.id) === String(form.subcategoryId)
    );
    setValidationError('');
    setSubmitError('');
    setIsSaving(true);
    try {
      await onSubmit?.({
        productName,
        ...(standardCounts ? { standardCounts } : {}),
        categoryId: selectedCategory.id,
        subcategoryId: selectedSubcategory?.id ?? null,
        barcodeScope: form.barcodeScope,
        useDefaultTaxes: Boolean(form.useDefaultTaxes),
        taxIds: form.useDefaultTaxes ? [] : [...form.taxIds],
        productImageFile: imageFile,
        removeProductImage,
      });
    } catch (error) {
      if (error?.response?.data?.code === 'standard_counts_required') {
        setStandardCounts(error.response.data.standardCountTemplate.map(row => ({ ...row, quantity: '' })));
      }
      setSubmitError(getErrorMessage(
        error,
        isEditing ? 'No se pudo actualizar el producto.' : 'No se pudo crear el producto.'
      ));
    } finally {
      setIsSaving(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[1700] flex items-stretch justify-center bg-slate-950/65 p-0 backdrop-blur-[1px] sm:items-center sm:p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) requestClose();
      }}
    >
      <section
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="product-form-dialog-title"
        aria-describedby="product-form-dialog-description"
        aria-busy={isSaving}
        className="flex h-[100dvh] w-full flex-col overflow-hidden bg-white shadow-2xl sm:h-auto sm:max-h-[calc(100dvh-2rem)] sm:max-w-4xl sm:rounded-2xl sm:border sm:border-gray-100"
        onMouseDown={(event) => event.stopPropagation()}
        onKeyDown={handleDialogKeyDown}
      >
        <header
          className="flex shrink-0 items-start justify-between gap-4 border-b border-gray-200 bg-gray-50 px-4 pb-4 pt-4 sm:px-6"
          style={{ paddingTop: 'calc(1rem + env(safe-area-inset-top))' }}
        >
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">Catalogo</p>
            <h2 id="product-form-dialog-title" className="mt-1 text-xl font-semibold text-gray-950">
              {isEditing ? 'Editar producto' : 'Crear producto'}
            </h2>
            <p id="product-form-dialog-description" className="mt-1 text-sm text-gray-600">
              Define la identidad del catálogo. Los precios, costos y márgenes se administran en Lista de precios.
            </p>
          </div>
          <button
            type="button"
            onClick={requestClose}
            disabled={isSaving}
            aria-label="Cerrar formulario de producto"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 shadow-sm hover:bg-gray-100 hover:text-gray-900 disabled:opacity-50"
          >
            <FiX aria-hidden="true" />
          </button>
        </header>

        <form className="flex min-h-0 flex-1 flex-col" onSubmit={handleSubmit}>
          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6 sm:py-6">


            <div className="grid gap-6 lg:grid-cols-[220px_minmax(0,1fr)]">
              <aside className="h-fit rounded-2xl border border-gray-200 bg-gray-50 p-3 sm:p-4">
                <div className="flex items-center gap-3 lg:block">
                  <div className="relative h-24 w-24 shrink-0 overflow-hidden rounded-xl border border-gray-200 bg-white lg:h-auto lg:w-full lg:aspect-square">
                    {displayedImage ? (
                      <img src={displayedImage} alt="Vista previa del producto" className="absolute inset-0 h-full w-full object-cover" />
                    ) : (
                      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-gray-400">
                        <FiImage className="text-2xl" aria-hidden="true" />
                        <span className="text-[11px] font-medium">Sin imagen</span>
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1 lg:mt-3">
                    <div className="text-sm font-semibold text-gray-900">Imagen</div>
                    <p className="mt-1 text-xs leading-relaxed text-gray-500">JPG, PNG o WebP.</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <label className={`inline-flex cursor-pointer items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-700 ${disabled ? 'pointer-events-none opacity-50' : ''}`}>
                        <FiUpload aria-hidden="true" />
                        Elegir imagen
                        <input
                          type="file"
                          accept="image/*"
                          className="sr-only"
                          disabled={disabled}
                          onChange={(event) => {
                            const file = event.target.files?.[0] || null;
                            setImageFile(file);
                            if (file) setRemoveProductImage(false);
                            event.target.value = '';
                          }}
                        />
                      </label>
                      {(imageFile || existingImage) && (
                        <button
                          type="button"
                          onClick={() => {
                            setImageFile(null);
                            setRemoveProductImage(true);
                          }}
                          disabled={disabled}
                          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                        >
                          Quitar
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </aside>

              <div className="space-y-6">
                <section aria-labelledby="product-general-heading" className="space-y-4">
                  <div>
                    <h3 id="product-general-heading" className="text-sm font-semibold text-gray-950">Datos generales</h3>
                    <p className="mt-1 text-xs text-gray-500">Nombre y ubicacion dentro del catalogo.</p>
                  </div>
                  <div>
                    <label htmlFor="product-form-name" className="text-xs font-semibold uppercase text-gray-600">Nombre</label>
                    <input
                      ref={nameInputRef}
                      id="product-form-name"
                      name="productName"
                      type="text"
                      value={form.productName}
                      onChange={(event) => {
                        setForm((current) => ({ ...current, productName: event.target.value }));
                        setValidationError('');
                      }}
                      autoComplete="off"
                      disabled={disabled}
                      className="mt-2 w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:bg-gray-100"
                    />
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label htmlFor="product-form-category" className="text-xs font-semibold uppercase text-gray-600">Categoria</label>
                      <select
                        id="product-form-category"
                        name="categoryId"
                        value={form.categoryId}
                        onChange={(event) => {
                          setForm((current) => ({ ...current, categoryId: event.target.value, subcategoryId: '' }));
                          setValidationError('');
                        }}
                        disabled={disabled}
                        className="mt-2 w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:bg-gray-100"
                      >
                        <option value="">Selecciona una categoria</option>
                        {categoryOptions.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.name}{category.isActive === false ? ' (inactiva)' : ''}
                          </option>
                        ))}
                      </select>
                      {categoriesLoading && <p role="status" className="mt-1 text-xs text-gray-500">Cargando categorias...</p>}
                      {!categoriesLoading && categoriesError && <p className="mt-1 text-xs text-red-600">{categoriesError}</p>}
                    </div>
                    <div>
                      <label htmlFor="product-form-subcategory" className="text-xs font-semibold uppercase text-gray-600">Subcategoria</label>
                      <select
                        id="product-form-subcategory"
                        name="subcategoryId"
                        value={form.subcategoryId}
                        onChange={(event) => setForm((current) => ({ ...current, subcategoryId: event.target.value }))}
                        disabled={disabled || !selectedCategory}
                        className="mt-2 w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:bg-gray-100"
                      >
                        <option value="">Sin subcategoria</option>
                        {subcategoryOptions.map((subcategory) => (
                          <option key={subcategory.id} value={subcategory.id}>{subcategory.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </section>

                <section aria-labelledby="product-operation-heading" className="space-y-4">
                  <div>
                    <h3 id="product-operation-heading" className="text-sm font-semibold text-gray-950">Operacion</h3>
                    <p className="mt-1 text-xs text-gray-500">Codigos de barra e impuestos aplicables.</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Los códigos de barras nuevos deben ser únicos en toda la empresa.</p>
                  </div>

                  <div className="rounded-2xl border border-gray-200 p-4">
                    <label className="flex items-start gap-3 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={form.useDefaultTaxes}
                        onChange={(event) => setForm((current) => ({ ...current, useDefaultTaxes: event.target.checked }))}
                        disabled={disabled || !canReadTaxes || taxesLoading || Boolean(taxesError)}
                        className="mt-0.5 accent-blue-600"
                      />
                      <span>
                        <span className="block font-semibold text-gray-900">Usar impuestos por defecto</span>
                        <span className="mt-0.5 block text-xs text-gray-500">Aplica la configuracion tributaria predeterminada del negocio.</span>
                      </span>
                    </label>
                    {!canReadTaxes && <p className="mt-3 text-xs text-gray-500">No tienes permisos para modificar impuestos; se conservara la configuracion actual.</p>}
                    {canReadTaxes && taxesLoading && <p role="status" className="mt-3 text-xs text-gray-500">Cargando impuestos...</p>}
                    {canReadTaxes && !taxesLoading && taxesError && <p className="mt-3 text-xs text-red-600">{taxesError}</p>}
                    {canReadTaxes && !taxesLoading && !taxesError && !form.useDefaultTaxes && (
                      <div className="mt-4 grid gap-2 sm:grid-cols-2">
                        {taxOptions.length === 0 && <p className="text-xs text-gray-500 sm:col-span-2">No hay impuestos activos en esta sucursal.</p>}
                        {taxOptions.map((tax) => {
                          const checked = form.taxIds.some((taxId) => String(taxId) === String(tax.id));
                          return (
                            <label key={tax.id} className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(event) => {
                                  setForm((current) => ({
                                    ...current,
                                    taxIds: event.target.checked
                                      ? [...current.taxIds, tax.id]
                                      : current.taxIds.filter((taxId) => String(taxId) !== String(tax.id)),
                                  }));
                                }}
                                disabled={disabled}
                                className="accent-blue-600"
                              />
                              {tax.name} ({Number(tax.rate || 0)}%)
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </section>

                {standardCounts && <fieldset className="m-4 space-y-3 rounded-xl border p-4"><legend>Conteo inicial de inventario</legend>{standardCounts.map((row, index) => <label className="block text-sm" key={`${row.branchId}:${row.varietyId}`}>{row.branchName} · {row.varietyName} ({row.unit})<input type="number" min="0" step="any" required inputMode="decimal" className="mt-1 w-full rounded border p-2" value={row.quantity} onChange={event => setStandardCounts(current => current.map((item, i) => i === index ? { ...item, quantity: event.target.value } : item))} /></label>)}</fieldset>}
            {(validationError || submitError) && (
                  <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {validationError || submitError}
                  </p>
                )}
              </div>
            </div>
          </div>

          <footer
            className="flex shrink-0 flex-col-reverse gap-3 border-t border-gray-200 bg-white px-4 pb-4 pt-3 sm:flex-row sm:items-center sm:justify-between sm:px-6"
            style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
          >
            <p className="text-center text-xs text-gray-500 sm:text-left">Los cambios se aplican al guardar.</p>
            <div className="grid grid-cols-2 gap-2 sm:flex">
              <button
                type="button"
                onClick={requestClose}
                disabled={isSaving}
                className="rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={disabled || !form.productName.trim() || !selectedCategory}
                className="rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isSaving ? 'Guardando...' : isEditing ? 'Guardar cambios' : 'Crear producto'}
              </button>
            </div>
          </footer>
        </form>
      </section>
    </div>,
    document.body
  );
};

export default ProductFormDialog;
